import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, List, ListItem, ListItemText, Divider } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Updated interface to include timestamp
interface Tweet {
  content: string;
  timestamp: string | null;
}

// Helper function to get content from a tweet
const getTweetContent = (tweet: Tweet): string => {
  return tweet.content || '';
};

interface ExpertTweetsProps {
  twitterHandle: string;
}

const ExpertTweets: React.FC<ExpertTweetsProps> = ({ twitterHandle }) => {
  const [tweets, setTweets] = useState<Tweet[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // IMPORTANT: Replace with your actual deployed Supabase Function URL
  const supabaseFunctionUrl = 'https://extzjgoxfssxcnucdetr.supabase.co/functions/v1/fetch-tweets';

  useEffect(() => {
    const fetchTweets = async () => {
      if (!twitterHandle) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Use POST method as Supabase functions often expect it
        const response = await fetch(supabaseFunctionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              handles: [twitterHandle] // Only request tweets for this specific expert
            })
        });

        if (!response.ok) {
          let errorMsg = `HTTP error! status: ${response.status}`;
          try {
              const errorData = await response.json();
              errorMsg = errorData.error || errorMsg;
          } catch (e) {
              // Ignore if response is not JSON
          }
          throw new Error(errorMsg);
        }
        
        const responseData = await response.json();
        console.log(`Raw API Response for ${twitterHandle}:`, responseData);

        if (responseData && typeof responseData === 'object' && responseData[twitterHandle]) {
          // Get only the tweets for this specific expert
          const expertTweets = responseData[twitterHandle].map((tweet: any) => ({
            content: tweet.content || '',
            timestamp: tweet.timestamp || null,
          }));
          setTweets(expertTweets);
        } else {
          setTweets([]);
        }
      } catch (err: any) {
        console.error(`Failed to fetch tweets for ${twitterHandle}:`, err);
        setError(err.message || 'Failed to load tweets. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTweets();
  }, [twitterHandle]); // Re-fetch when the twitter handle changes

  if (!twitterHandle) {
    return (
      <Typography variant="body2" color="text.secondary">
        No Twitter handle available for this expert.
      </Typography>
    );
  }

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress color="primary" size={30} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && tweets && tweets.length > 0 && (
        <Paper elevation={1} sx={{ maxHeight: 350, overflowY: 'auto' }}>
          <List dense>
            {tweets.map((tweet, index) => {
              const content = getTweetContent(tweet);
              const date = tweet.timestamp ? new Date(tweet.timestamp) : null;
              let timeLabel = 'No date available';

              if (date && !isNaN(date.getTime())) {
                timeLabel = dayjs(date).format('MMM DD, YYYY, hh:mm A');
              } else if (tweet.timestamp) {
                timeLabel = tweet.timestamp;
              }

              return (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            {timeLabel}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < tweets.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}
      
      {!loading && !error && (!tweets || tweets.length === 0) && (
        <Typography sx={{ textAlign: 'center', mt: 1 }} color="text.secondary">
          No tweets available from this expert at the moment.
        </Typography>
      )}
    </Box>
  );
};

export default ExpertTweets;